import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  PLANS, MOMENTS_ADDON, plansForTrack, isPlanUpgrade, formatMoneyUSD,
} from '../../constants/plans';
import { stripeApi } from '../../services/api';
import { useI18n } from '../../context/I18nContext';
import MomentsToggle from './MomentsToggle';

export default function UpgradeModal({ open, onClose, resource, currentPlan = null }) {
  const { t } = useI18n();
  const { user } = useSelector((s) => s.auth);
  const [annual, setAnnual] = useState(false);
  const [loadingKey, setLoadingKey] = useState(null);
  // Live Moments add-on, offered on every paywall; on by default when Moments is what they asked for.
  const [withMoments, setWithMoments] = useState(resource === 'moments');
  useEffect(() => {
    if (open) setWithMoments(resource === 'moments');
  }, [open, resource]);

  if (!open) return null;

  // Show upgrades within the user's current track (couples vs studios).
  const currentMeta = PLANS.find((p) => p.id === currentPlan);
  const track = currentMeta?.track || (resource === 'weddings' ? 'studio' : 'couple');
  // Only plans that are on sale — hidden/legacy ones (trial, previous couples plans) are excluded.
  const visiblePlans = plansForTrack(track);
  const showAnnualToggle = track === 'studio';

  // Paywall actions (see utils/paywall): the user hit a "get a link" step on a free account.
  const PAYWALL_COPY = {
    publish: ['paywall.publishTitle', 'Choose a plan to publish', 'paywall.publishBody', 'Your invitation is saved. Pick a plan to publish it and get the link to send to your guests.'],
    share: ['paywall.shareTitle', 'Choose a plan to share your gallery', 'paywall.shareBody', 'Your gallery is saved. Pick a plan to get a link your guests can open.'],
    moments: ['paywall.momentsTitle', 'Live Moments needs a plan', 'paywall.momentsBody', 'Pick a plan with Live Moments to get the guest upload link and QR code.'],
    create: ['paywall.createTitle', 'Your free draft is in use', 'paywall.createBody', 'Free accounts can design one invitation and one gallery. Pick a plan to create more.'],
  };
  const paywall = PAYWALL_COPY[resource];
  const showMomentsToggle = Boolean(paywall) && track === 'couple';

  const title = paywall
    ? t(paywall[0]) || paywall[1]
    : resource === 'guests'
    ? t('upgrade.guestLimitTitle') || 'Guest limit reached'
    : resource === 'photos'
      ? t('upgrade.photoLimitTitle') || 'Photo limit reached'
      : t('upgrade.weddingLimitTitle') || 'Wedding limit reached';

  const subtitle = paywall
    ? t(paywall[2]) || paywall[3]
    : resource === 'guests'
    ? t('upgrade.guestLimitBody') || 'Your current plan caps how many guests can RSVP as attending for this wedding.'
    : resource === 'photos'
      ? t('upgrade.photoLimitBody') || 'Your plan limits how many full-quality photos you can store per gallery. Upgrade for a higher cap.'
      : t('upgrade.weddingLimitBody') || 'Upgrade to create more weddings on one account.';

  const startCheckout = async (planId) => {
    if (!user?.isVerified) {
      toast.error(t('upgrade.verifyFirst') || 'Please confirm your email before subscribing.');
      return;
    }
    const planData = PLANS.find((p) => p.id === planId);
    const billingInterval = planData?.billingType === 'oneTime' ? 'once' : annual ? 'year' : 'month';
    setLoadingKey(`${planId}-${billingInterval}`);
    try {
      // Buy the plan with the Live Moments add-on in the same checkout when the switch is on.
      const addMoments = showMomentsToggle && withMoments && Boolean(MOMENTS_ADDON[planId]);
      const { data } = await stripeApi.createCheckoutSession(planId, billingInterval, undefined, addMoments);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(t('upgrade.noCheckoutUrl') || 'No checkout URL returned');
    } catch (e) {
      toast.error(e.response?.data?.message || t('upgrade.checkoutFailed') || 'Could not start checkout. Is Stripe configured on the server?');
    } finally {
      setLoadingKey(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
    >
      <div className="bg-white dark:bg-[#1e1e2a] rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-[#E2DCD7] dark:border-[#2a2a3a]">
        <div className="p-6 border-b border-[#ECE7E3] dark:border-[#2a2a3a]">
          <h2 className="font-playfair text-2xl text-[#1C1A22] dark:text-stone-100">{title}</h2>
          <p className="text-[#6B6672] dark:text-stone-400 text-sm mt-2">{subtitle}</p>
        </div>
        <div className="p-6 space-y-3">
          {!user?.isVerified && (
            <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
              {t('upgrade.verifyEmailNote') || 'Confirm your email address to subscribe. Check your inbox or resend from the dashboard.'}
            </p>
          )}
          {showAnnualToggle && (
            <div className="inline-flex rounded-full border border-[#E2DCD7] dark:border-[#2a2a3a] bg-[#F7F4F2] dark:bg-[#252535] p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setAnnual(false)}
                className={`px-3 py-1 rounded-full ${!annual ? 'bg-[#9A4357] dark:bg-[#9A4357] text-white dark:text-white' : 'text-[#4A4651] dark:text-stone-400'}`}
              >
                {t('upgrade.monthly') || 'Monthly'}
              </button>
              <button
                type="button"
                onClick={() => setAnnual(true)}
                className={`px-3 py-1 rounded-full ${annual ? 'bg-[#9A4357] dark:bg-[#9A4357] text-white dark:text-white' : 'text-[#4A4651] dark:text-stone-400'}`}
              >
                {t('upgrade.annualDiscount') || 'Annual −20%'}
              </button>
            </div>
          )}
          {showMomentsToggle && (
            <MomentsToggle checked={withMoments} onChange={setWithMoments} t={t} />
          )}
          <p className="text-xs font-semibold text-[#6B6672] dark:text-stone-400 uppercase tracking-wide">
            {t('upgrade.comparePlans') || 'Compare plans'}
          </p>
          <div className="space-y-2">
            {visiblePlans.map((p) => {
              const active = p.id === currentPlan;
              const canUpgrade = isPlanUpgrade(currentPlan, p.id);
              const isOnce = p.billingType === 'oneTime';
              // With the Moments switch on, checkout is plan + add-on, so the button shows the combined price.
              const addonPrice = showMomentsToggle && withMoments ? (MOMENTS_ADDON[p.id]?.priceOnce || 0) : 0;
              const photos = typeof p.photosPerGallery === 'number' ? p.photosPerGallery.toLocaleString() : p.photosPerGallery;
              return (
                <div
                  key={p.id}
                  className={`rounded-xl border px-3 py-2 text-sm flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 ${
                    active ? 'border-[#9A4357] dark:border-[#E8A5B4] bg-[#F7F4F2] dark:bg-[#2a2a3a]' : 'border-[#ECE7E3] dark:border-[#2a2a3a]'
                  }`}
                >
                  <div>
                    <span className="font-medium text-[#1C1A22] dark:text-stone-100">{p.labelKey ? t(p.labelKey) : p.label}</span>
                    <span className="text-[#6B6672] dark:text-stone-400 text-xs block sm:inline sm:ml-2">
                      {p.storageGB >= 1024 ? `${p.storageGB / 1024} TB` : `${p.storageGB} GB`} storage · {p.guests} guests · {photos} photos
                    </span>
                    {isOnce && (
                      <span className="text-[#9A4357] dark:text-[#E8A5B4] text-xs block sm:inline sm:ml-2 font-medium">
                        {t('upgrade.oneTime') || 'one-time'}
                      </span>
                    )}
                  </div>
                  {canUpgrade && (
                    <button
                      type="button"
                      disabled={Boolean(loadingKey)}
                      onClick={() => startCheckout(p.id)}
                      className="shrink-0 px-3 py-1.5 rounded-full bg-[#9A4357] dark:bg-[#9A4357] text-white dark:text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50"
                    >
                      {(loadingKey === `${p.id}-year` || loadingKey === `${p.id}-month` || loadingKey === `${p.id}-once`)
                        ? (t('upgrade.redirecting') || 'Redirecting…')
                        : isOnce
                          ? (t('upgrade.buyOnce', { price: formatMoneyUSD(p.priceOnce + addonPrice) }) || `Buy — ${formatMoneyUSD(p.priceOnce + addonPrice)} once`)
                          : annual
                            ? (t('upgrade.subscribeAnnual', { price: formatMoneyUSD(p.priceAnnual / 12) }) || `Subscribe — ${formatMoneyUSD(p.priceAnnual / 12)}/mo billed yr`)
                            : (t('upgrade.subscribeMonthly', { price: formatMoneyUSD(p.priceMonthly) }) || `Subscribe — ${formatMoneyUSD(p.priceMonthly)}/mo`)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-xs text-[#8E8893] dark:text-stone-500 pt-2">
            {t('upgrade.stripeNote') || 'Secure checkout powered by Stripe. You can manage billing from the dashboard after subscribing.'}
          </p>
        </div>
        <div className="p-6 border-t border-[#ECE7E3] dark:border-[#2a2a3a] flex flex-col sm:flex-row gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-[#E2DCD7] dark:border-[#2a2a3a] text-[#4A4651] dark:text-stone-300 text-sm hover:bg-[#F7F4F2] dark:hover:bg-[#2a2a3a]"
          >
            {t('upgrade.notNow') || 'Not now'}
          </button>
          <Link
            to="/pricing"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-[#9A4357] dark:bg-[#9A4357] text-white dark:text-white text-sm font-medium text-center hover:opacity-90"
          >
            {t('upgrade.fullPricing') || 'Full pricing page'}
          </Link>
        </div>
      </div>
    </div>
  );
}
